import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../config';

const ContractFeeSettings = ({ contractVuid, contractName, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [loadingFeeLevel, setLoadingFeeLevel] = useState(false);
  const [feeSettings, setFeeSettings] = useState([]);
  const [filteredSettings, setFilteredSettings] = useState([]);
  const [feeLevel, setFeeLevel] = useState(null); // Start as null to prevent showing wrong UI
  const [feeCostCodeOptions, setFeeCostCodeOptions] = useState([]);
  const [selectedFeeCostCode, setSelectedFeeCostCode] = useState(null);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const hasLoadedRef = useRef(false);
  const isLoadingRef = useRef(false);
  
  // Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [costTypeFilter, setCostTypeFilter] = useState('');
  const [costCodeFilter, setCostCodeFilter] = useState('');
  
  // Selection states
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [selectAll, setSelectAll] = useState(false);
  
  // Bulk update state
  const [showBulkUpdate, setShowBulkUpdate] = useState(false);
  const [bulkFeePercentage, setBulkFeePercentage] = useState('');
  
  // Track unsaved fee percentage changes per setting
  const [unsavedFeeChanges, setUnsavedFeeChanges] = useState({});
  
  // Get unique cost types and cost codes for filters
  const uniqueCostTypes = [...new Set(feeSettings.map(s => s.cost_type?.cost_type || s.cost_type).filter(Boolean))];
  const uniqueCostCodes = [...new Set(feeSettings.map(s => s.cost_code?.code || s.cost_code).filter(Boolean))];

  useEffect(() => {
    // Only load once when contractVuid changes
    if (hasLoadedRef.current || isLoadingRef.current) return;
    
    const loadData = async () => {
      isLoadingRef.current = true;
      hasLoadedRef.current = true;
      
      try {
        fetchFeeSettings();
        const options = await fetchFeeCostCodeOptions();
        // Fetch fee level with the options we just got
        await fetchFeeLevel(options);
      } finally {
        isLoadingRef.current = false;
      }
    };
    loadData();
    
    return () => {
      hasLoadedRef.current = false;
      isLoadingRef.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contractVuid]);

  useEffect(() => {
    filterSettings();
  }, [feeSettings, searchTerm, costTypeFilter, costCodeFilter]);

  const fetchFeeSettings = async () => {
    try {
      setLoading(true);
      setError('');
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/api/contracts/${contractVuid}/fee-settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFeeSettings(response.data || []);
      setFilteredSettings(response.data || []);
      // Clear unsaved changes when data is refreshed
      setUnsavedFeeChanges({});
    } catch (err) {
      console.error('Error fetching fee settings:', err);
      setError('Failed to fetch fee settings: ' + (err.response?.data?.error || err.message));
    } finally {
      setLoading(false);
    }
  };

  const filterSettings = () => {
    let filtered = [...feeSettings];

    // Fuzzy search - search in cost code, cost type, or description
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(setting => {
        const costCode = setting.cost_code?.code || setting.cost_code || '';
        const costCodeDesc = setting.cost_code?.description || '';
        const costType = setting.cost_type?.cost_type || setting.cost_type || '';
        const costTypeAbbr = setting.cost_type?.abbreviation || '';
        const codeMatch = costCode.toLowerCase().includes(searchLower);
        const codeDescMatch = costCodeDesc.toLowerCase().includes(searchLower);
        const typeMatch = costType.toLowerCase().includes(searchLower);
        const typeAbbrMatch = costTypeAbbr.toLowerCase().includes(searchLower);
        return codeMatch || codeDescMatch || typeMatch || typeAbbrMatch;
      });
    }

    // Cost type filter
    if (costTypeFilter) {
      filtered = filtered.filter(setting => {
        const costType = setting.cost_type?.cost_type || setting.cost_type || '';
        return costType === costTypeFilter;
      });
    }

    // Cost code filter
    if (costCodeFilter) {
      filtered = filtered.filter(setting => {
        const costCode = setting.cost_code?.code || setting.cost_code || '';
        return costCode === costCodeFilter;
      });
    }

    setFilteredSettings(filtered);
    // Reset select all when filtering
    setSelectAll(false);
    setSelectedRows(new Set());
  };

  const fetchFeeLevel = async (options = null) => {
    // Prevent multiple simultaneous calls
    if (isLoadingRef.current) {
      console.log('fetchFeeLevel already in progress, skipping');
      return;
    }
    
    try {
      isLoadingRef.current = true;
      setLoadingFeeLevel(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/api/contracts/${contractVuid}/fee-level`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      console.log('Fee level response:', response.data);
      
      // Use provided options or current state
      const currentOptions = options || feeCostCodeOptions;
      
      if (response.data) {
        const newFeeLevel = response.data.fee_level || 'contract_item';
        setFeeLevel(newFeeLevel);
        
        // Only update if we have saved values
        if (response.data.fee_cost_code_vuid && response.data.fee_cost_type_vuid) {
          // Try to find the matching option from currentOptions to get all fields
          const matchingOption = currentOptions.find(
            opt => opt.cost_code_vuid === response.data.fee_cost_code_vuid && 
                    opt.cost_type_vuid === response.data.fee_cost_type_vuid
          );
          
          if (matchingOption) {
            setSelectedFeeCostCode(matchingOption);
          } else if (currentOptions.length > 0) {
            // Options are loaded but no match - use fallback
            const fallback = {
              cost_code_vuid: response.data.fee_cost_code_vuid,
              cost_type_vuid: response.data.fee_cost_type_vuid,
              cost_code: response.data.fee_cost_code || 'Unknown',
              cost_type: response.data.fee_cost_type || 'Unknown'
            };
            setSelectedFeeCostCode(fallback);
          } else {
            // Options not loaded yet - store the VUIDs
            const fallback = {
              cost_code_vuid: response.data.fee_cost_code_vuid,
              cost_type_vuid: response.data.fee_cost_type_vuid,
              cost_code: response.data.fee_cost_code || 'Loading...',
              cost_type: response.data.fee_cost_type || 'Loading...'
            };
            setSelectedFeeCostCode(fallback);
          }
        } else {
          // No saved values - clear selection
          setSelectedFeeCostCode(null);
        }
      }
    } catch (err) {
      console.error('Error fetching fee level:', err);
      // Don't set error here, just use default
    } finally {
      setLoadingFeeLevel(false);
      isLoadingRef.current = false;
    }
  };

  const fetchFeeCostCodeOptions = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/api/contracts/${contractVuid}/fee-cost-code-options`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      console.log('Fee cost code options response:', response.data);
      const options = response.data || [];
      setFeeCostCodeOptions(options);
      return options;
    } catch (err) {
      console.error('Error fetching fee cost code options:', err);
      return [];
    }
  };

  const handleFeeLevelChange = async (newLevel) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API_BASE_URL}/api/contracts/${contractVuid}/fee-level`, {
        fee_level: newLevel,
        fee_cost_code_vuid: newLevel === 'fee_cost_code' ? (selectedFeeCostCode?.cost_code_vuid || null) : null,
        fee_cost_type_vuid: newLevel === 'fee_cost_code' ? (selectedFeeCostCode?.cost_type_vuid || null) : null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFeeLevel(newLevel);
      if (newLevel === 'contract_item') {
        setSelectedFeeCostCode(null);
      }
      setSuccessMessage('Fee level updated successfully');
      setTimeout(() => setSuccessMessage(''), 3000);
      // Don't refetch - we just updated it
    } catch (err) {
      console.error('Error updating fee level:', err);
      setError('Failed to update fee level: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleFeeCostCodeSelection = async (option) => {
    try {
      const token = localStorage.getItem('token');
      console.log('=== handleFeeCostCodeSelection called ===');
      console.log('Option received:', option);
      console.log('Option cost_code_vuid:', option?.cost_code_vuid);
      console.log('Option cost_type_vuid:', option?.cost_type_vuid);
      
      if (!option || !option.cost_code_vuid || !option.cost_type_vuid) {
        console.error('Invalid option provided:', option);
        setError('Invalid option selected. Please try again.');
        return;
      }
      
      // Immediately update local state for better UX
      setSelectedFeeCostCode(option);
      
      const payload = {
        fee_level: 'fee_cost_code',
        fee_cost_code_vuid: option.cost_code_vuid,
        fee_cost_type_vuid: option.cost_type_vuid
      };
      console.log('Sending PUT request with payload:', payload);
      console.log('URL:', `${API_BASE_URL}/api/contracts/${contractVuid}/fee-level`);
      
      const response = await axios.put(`${API_BASE_URL}/api/contracts/${contractVuid}/fee-level`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      console.log('Fee cost code save response:', response.data);
      console.log('Response status:', response.status);
      
      // Refresh fee level to ensure we have the latest data from server
      // Pass current options to avoid closure issues
      await fetchFeeLevel(feeCostCodeOptions);
      
      setError(''); // Clear any previous errors
      setSuccessMessage('Fee cost code updated successfully');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('=== ERROR in handleFeeCostCodeSelection ===');
      console.error('Error object:', err);
      console.error('Error message:', err.message);
      console.error('Error response:', err.response?.data);
      console.error('Error status:', err.response?.status);
      
      // Revert state on error
      setSelectedFeeCostCode(null);
      await fetchFeeLevel(); // Refresh to get actual saved state
      
      setError('Failed to update fee cost code: ' + (err.response?.data?.error || err.message));
      setSuccessMessage('');
    }
  };

  const handleFeePercentageInputChange = (setting, newValue) => {
    // Store the change in local state only (don't save yet)
    const settingKey = `${setting.cost_code_vuid}-${setting.cost_type_vuid}`;
    setUnsavedFeeChanges(prev => ({
      ...prev,
      [settingKey]: newValue === '' ? null : parseFloat(newValue) || 0
    }));
  };

  const handleFeePercentageSave = async (setting) => {
    const settingKey = `${setting.cost_code_vuid}-${setting.cost_type_vuid}`;
    const newValue = unsavedFeeChanges[settingKey];
    
    console.log('handleFeePercentageSave called', { setting, settingKey, newValue, unsavedFeeChanges });
    
    if (newValue === undefined) {
      // No unsaved changes
      console.log('No unsaved changes, returning early');
      return;
    }

    try {
      const feePercentage = newValue;
      const token = localStorage.getItem('token');
      
      console.log('Saving fee percentage', { 
        setting_vuid: setting.vuid, 
        feePercentage, 
        contractVuid,
        hasVuid: !!setting.vuid 
      });
      
      const payload = {
        cost_code_vuid: setting.cost_code_vuid,
        cost_type_vuid: setting.cost_type_vuid,
        fee_percentage: feePercentage,
        default_fee_percentage: setting.default_fee_percentage || 0
      };

      let response;
      if (setting.vuid) {
        // Update existing
        console.log('Updating existing setting via PUT', `${API_BASE_URL}/api/contracts/${contractVuid}/fee-settings/${setting.vuid}`);
        response = await axios.put(
          `${API_BASE_URL}/api/contracts/${contractVuid}/fee-settings/${setting.vuid}`,
          { fee_percentage: feePercentage },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        console.log('PUT response:', response.data);
      } else {
        // Create new
        console.log('Creating new setting via POST', `${API_BASE_URL}/api/contracts/${contractVuid}/fee-settings`);
        response = await axios.post(
          `${API_BASE_URL}/api/contracts/${contractVuid}/fee-settings`,
          payload,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        console.log('POST response:', response.data);
      }

      // Update local state
      setFeeSettings(prev => prev.map(s => 
        s.cost_code_vuid === setting.cost_code_vuid && 
        s.cost_type_vuid === setting.cost_type_vuid
          ? { ...s, ...response.data, fee_percentage: feePercentage, is_override: feePercentage !== (setting.default_fee_percentage || 0) }
          : s
      ));

      // Clear unsaved change
      setUnsavedFeeChanges(prev => {
        const updated = { ...prev };
        delete updated[settingKey];
        return updated;
      });

      setSuccessMessage('Fee percentage updated successfully');
      setTimeout(() => setSuccessMessage(''), 3000);
      
      // Refresh to get latest data
      await fetchFeeSettings();
    } catch (err) {
      console.error('Error updating fee percentage:', err);
      console.error('Error response:', err.response);
      setError(err.response?.data?.error || err.message || 'Failed to update fee percentage');
      setTimeout(() => setError(''), 5000);
    }
  };

  const handleUpdateFee = async (setting) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API_BASE_URL}/api/contracts/${contractVuid}/fee-settings/${setting.vuid}`, {
        fee_percentage: setting.fee_percentage
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuccessMessage('Fee updated successfully');
      setTimeout(() => setSuccessMessage(''), 3000);
      fetchFeeSettings();
    } catch (err) {
      console.error('Error updating fee:', err);
      setError('Failed to update fee: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleSelectRow = (index) => {
    const newSelected = new Set(selectedRows);
    if (newSelected.has(index)) {
      newSelected.delete(index);
    } else {
      newSelected.add(index);
    }
    setSelectedRows(newSelected);
    setSelectAll(newSelected.size === filteredSettings.length);
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedRows(new Set());
    } else {
      setSelectedRows(new Set(filteredSettings.map((_, idx) => idx)));
    }
    setSelectAll(!selectAll);
  };

  const handleBulkUpdate = async () => {
    if (!bulkFeePercentage || selectedRows.size === 0) {
      setError('Please select rows and enter a fee percentage');
      return;
    }

    try {
      const feePercentage = parseFloat(bulkFeePercentage);
      if (isNaN(feePercentage)) {
        setError('Please enter a valid number');
        return;
      }

      const token = localStorage.getItem('token');
      const updates = Array.from(selectedRows).map(idx => {
        const setting = filteredSettings[idx];
        return {
          cost_code_vuid: setting.cost_code_vuid,
          cost_type_vuid: setting.cost_type_vuid,
          fee_percentage: feePercentage,
          default_fee_percentage: setting.default_fee_percentage
        };
      });

      await axios.put(
        `${API_BASE_URL}/api/contracts/${contractVuid}/fee-settings/bulk`,
        { updates },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Refresh data
      await fetchFeeSettings();
      setSelectedRows(new Set());
      setSelectAll(false);
      setShowBulkUpdate(false);
      setBulkFeePercentage('');
      setSuccessMessage(`Updated ${updates.length} fee setting(s) successfully`);
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('Error bulk updating fee settings:', err);
      setError(err.response?.data?.error || 'Failed to bulk update fee settings');
      setTimeout(() => setError(''), 5000);
    }
  };

  const handleBulkUpdateAllFiltered = async () => {
    if (!bulkFeePercentage || filteredSettings.length === 0) {
      setError('Please enter a fee percentage');
      return;
    }

    try {
      const feePercentage = parseFloat(bulkFeePercentage);
      if (isNaN(feePercentage)) {
        setError('Please enter a valid number');
        return;
      }

      const token = localStorage.getItem('token');
      const updates = filteredSettings.map(setting => {
        console.log('Bulk update - mapping setting:', {
          cost_code_vuid: setting.cost_code_vuid,
          cost_type_vuid: setting.cost_type_vuid,
          current_fee: setting.fee_percentage,
          default_fee: setting.default_fee_percentage,
          new_fee: feePercentage
        });
        return {
          cost_code_vuid: setting.cost_code_vuid,
          cost_type_vuid: setting.cost_type_vuid,
          fee_percentage: feePercentage,
          default_fee_percentage: setting.default_fee_percentage
        };
      });

      console.log('Sending bulk update request:', { updates: updates.length, feePercentage });
      const response = await axios.put(
        `${API_BASE_URL}/api/contracts/${contractVuid}/fee-settings/bulk`,
        { updates },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      console.log('Bulk update response:', response.data);

      // Refresh data
      await fetchFeeSettings();
      setShowBulkUpdate(false);
      setBulkFeePercentage('');
      setSuccessMessage(`Updated ${response.data?.total || updates.length} fee setting(s) successfully`);
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('Error bulk updating all filtered fee settings:', err);
      console.error('Error details:', err.response?.data);
      setError(err.response?.data?.error || 'Failed to bulk update fee settings');
      setTimeout(() => setError(''), 5000);
    }
  };

  // Show loading if we haven't loaded fee level yet (prevents showing wrong UI)
  if (loading || loadingFeeLevel || feeLevel === null) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-8">
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto">
      <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full mx-4 my-8 max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between z-10">
          <h2 className="text-2xl font-bold text-gray-900">Contract Fee Settings</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-2xl font-bold"
          >
            ×
          </button>
        </div>

        <div className="p-6">
          <div className="mb-6">
            <p className="text-sm text-gray-600 mb-4">Contract: {contractName}</p>
          </div>

          {error && (
            <div className="mb-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded">
              {error}
            </div>
          )}

          {successMessage && (
            <div className="mb-4 p-4 bg-green-100 border border-green-400 text-green-700 rounded">
              {successMessage}
            </div>
          )}

          {/* Fee Level Selection */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Fee Level
            </label>
            <select
              value={feeLevel}
              onChange={(e) => handleFeeLevelChange(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-vermillion-500 focus:border-vermillion-500"
            >
              <option value="contract_item">Contract Item</option>
              <option value="fee_cost_code">Fee Cost Code</option>
            </select>
          </div>

          {/* Fee Cost Code Selection (only shown when fee_level is 'fee_cost_code') */}
          {feeLevel === 'fee_cost_code' && (
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Fee Cost Code
              </label>
              <select
                value={selectedFeeCostCode ? `${selectedFeeCostCode.cost_code_vuid}|||${selectedFeeCostCode.cost_type_vuid}` : ''}
                onChange={async (e) => {
                  console.log('=== Dropdown onChange triggered ===');
                  console.log('Selected value:', e.target.value);
                  console.log('Available options:', feeCostCodeOptions.length);
                  
                  if (e.target.value) {
                    // Use ||| as separator since UUIDs contain dashes
                    const parts = e.target.value.split('|||');
                    if (parts.length !== 2) {
                      console.error('Invalid value format, expected format: uuid|||uuid, got:', e.target.value);
                      setError('Invalid selection format. Please try again.');
                      return;
                    }
                    
                    const [costCodeVuid, costTypeVuid] = parts;
                    console.log('Split values - costCodeVuid:', costCodeVuid, 'costTypeVuid:', costTypeVuid);
                    
                    const option = feeCostCodeOptions.find(
                      opt => opt.cost_code_vuid === costCodeVuid && opt.cost_type_vuid === costTypeVuid
                    );
                    console.log('Found option:', option);
                    
                    if (option) {
                      console.log('Calling handleFeeCostCodeSelection with option');
                      await handleFeeCostCodeSelection(option);
                    } else {
                      console.error('Option not found in feeCostCodeOptions!');
                      console.error('Searched for:', { costCodeVuid, costTypeVuid });
                      console.error('Available options:', feeCostCodeOptions);
                      setError('Selected option not found. Please try again.');
                    }
                  } else {
                    console.log('Clearing selection');
                    await handleFeeCostCodeSelection({ cost_code_vuid: null, cost_type_vuid: null });
                  }
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-vermillion-500 focus:border-vermillion-500"
              >
                <option value="">Select a cost code and cost type combination</option>
                {feeCostCodeOptions.map((option, idx) => (
                  <option
                    key={idx}
                    value={`${option.cost_code_vuid}|||${option.cost_type_vuid}`}
                  >
                    {option.cost_code} - {option.cost_type} {option.cost_code_description ? `(${option.cost_code_description})` : ''}
                  </option>
                ))}
              </select>
              {selectedFeeCostCode ? (
                <p className="mt-2 text-sm text-gray-600">
                  Selected: {selectedFeeCostCode.cost_code} - {selectedFeeCostCode.cost_type} {selectedFeeCostCode.cost_code_description ? `(${selectedFeeCostCode.cost_code_description})` : ''}
                </p>
              ) : (
                <p className="mt-2 text-sm text-red-600">
                  No cost code selected. Please select one from the dropdown above.
                </p>
              )}
              <div className="mt-2 text-xs text-gray-500">
                Debug: selectedFeeCostCode = {selectedFeeCostCode ? JSON.stringify(selectedFeeCostCode) : 'null'}
              </div>
            </div>
          )}

          {/* Fee Settings Table (only shown when fee_level is 'contract_item') */}
          {feeLevel === 'contract_item' && feeLevel !== null && (
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Fee Settings by Cost Code and Cost Type</h3>
              
              {/* Filters */}
              <div className="bg-white rounded-xl shadow-2xl p-6 mb-6 border border-gray-200">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  {/* Fuzzy Search */}
                  <div className="md:col-span-2">
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Search (Cost Code, Cost Type)
                    </label>
                    <input
                      type="text"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      placeholder="e.g., 00, 01, Labor, Materials..."
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-800 focus:border-transparent"
                    />
                  </div>

                  {/* Cost Type Filter */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Cost Type
                    </label>
                    <select
                      value={costTypeFilter}
                      onChange={(e) => setCostTypeFilter(e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-800 focus:border-transparent"
                    >
                      <option value="">All Cost Types</option>
                      {uniqueCostTypes.map(type => (
                        <option key={type} value={type}>{type}</option>
                      ))}
                    </select>
                  </div>

                  {/* Cost Code Filter */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Cost Code
                    </label>
                    <select
                      value={costCodeFilter}
                      onChange={(e) => setCostCodeFilter(e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-800 focus:border-transparent"
                    >
                      <option value="">All Cost Codes</option>
                      {uniqueCostCodes.map(code => (
                        <option key={code} value={code}>{code}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Clear Filters */}
                {(searchTerm || costTypeFilter || costCodeFilter) && (
                  <div className="mt-4">
                    <button
                      onClick={() => {
                        setSearchTerm('');
                        setCostTypeFilter('');
                        setCostCodeFilter('');
                      }}
                      className="px-4 py-2 bg-gray-200 text-gray-700 font-semibold rounded-lg hover:bg-gray-300 transition-colors"
                    >
                      Clear Filters
                    </button>
                  </div>
                )}
              </div>

              {/* Bulk Update Section */}
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-6 mb-6">
                <div className="flex items-center justify-between flex-wrap gap-4">
                  <div className="flex-1">
                    {selectedRows.size > 0 ? (
                      <>
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">
                          {selectedRows.size} row(s) selected
                        </h3>
                        {!showBulkUpdate ? (
                          <div className="flex items-center space-x-3">
                            <button
                              onClick={() => setShowBulkUpdate(true)}
                              className="px-4 py-2 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-colors"
                            >
                              Bulk Update Selected
                            </button>
                            <button
                              onClick={() => {
                                setSelectedRows(new Set());
                                setSelectAll(false);
                              }}
                              className="px-4 py-2 bg-gray-200 text-gray-700 font-semibold rounded-lg hover:bg-gray-300 transition-colors"
                            >
                              Clear Selection
                            </button>
                          </div>
                        ) : (
                          <div className="flex items-center space-x-4">
                            <input
                              type="number"
                              value={bulkFeePercentage}
                              onChange={(e) => setBulkFeePercentage(e.target.value)}
                              placeholder="Fee %"
                              step="0.01"
                              className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600 w-32"
                            />
                            <button
                              onClick={handleBulkUpdate}
                              className="px-4 py-2 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-colors"
                            >
                              Apply to Selected
                            </button>
                            <button
                              onClick={() => {
                                setShowBulkUpdate(false);
                                setBulkFeePercentage('');
                              }}
                              className="px-4 py-2 bg-gray-200 text-gray-700 font-semibold rounded-lg hover:bg-gray-300 transition-colors"
                            >
                              Cancel
                            </button>
                          </div>
                        )}
                      </>
                    ) : (
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">
                          Update All Filtered Rows
                        </h3>
                        <p className="text-sm text-gray-600 mb-3">
                          {filteredSettings.length} row(s) match your current filter
                        </p>
                        {!showBulkUpdate ? (
                          <button
                            onClick={() => setShowBulkUpdate(true)}
                            className="px-4 py-2 bg-purple-600 text-white font-semibold rounded-lg hover:bg-purple-700 transition-colors"
                          >
                            Update All Filtered ({filteredSettings.length})
                          </button>
                        ) : (
                          <div className="flex items-center space-x-4">
                            <input
                              type="number"
                              value={bulkFeePercentage}
                              onChange={(e) => setBulkFeePercentage(e.target.value)}
                              placeholder="Fee %"
                              step="0.01"
                              className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-600 w-32"
                            />
                            <button
                              onClick={handleBulkUpdateAllFiltered}
                              className="px-4 py-2 bg-purple-600 text-white font-semibold rounded-lg hover:bg-purple-700 transition-colors"
                            >
                              Apply to All Filtered ({filteredSettings.length})
                            </button>
                            <button
                              onClick={() => {
                                setShowBulkUpdate(false);
                                setBulkFeePercentage('');
                              }}
                              className="px-4 py-2 bg-gray-200 text-gray-700 font-semibold rounded-lg hover:bg-gray-300 transition-colors"
                            >
                              Cancel
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Table */}
              {filteredSettings.length === 0 ? (
                <p className="text-gray-500">No fee settings found. Add contract items with cost codes and cost types to get started.</p>
              ) : (
                <div className="bg-white rounded-xl shadow-2xl p-6 border border-gray-200">
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            <input
                              type="checkbox"
                              checked={selectAll}
                              onChange={handleSelectAll}
                              className="rounded border-gray-300 text-gray-800 focus:ring-gray-800"
                            />
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Cost Code
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Cost Type
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Default Fee %
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Fee % (Override)
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Status
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Last Updated
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {filteredSettings.map((setting, index) => {
                          const costCode = setting.cost_code?.code || setting.cost_code || 'N/A';
                          const costCodeDesc = setting.cost_code?.description || '';
                          const costType = setting.cost_type?.cost_type || setting.cost_type || 'N/A';
                          const costTypeAbbr = setting.cost_type?.abbreviation || '';
                          const defaultFee = setting.default_fee_percentage || 0;
                          const feePercentage = setting.fee_percentage !== undefined ? setting.fee_percentage : defaultFee;
                          const isOverride = setting.is_override || (feePercentage !== defaultFee);
                          
                          return (
                            <tr
                              key={setting.vuid || `${setting.cost_code_vuid}-${setting.cost_type_vuid}`}
                              className={selectedRows.has(index) ? 'bg-blue-50' : 'hover:bg-gray-50'}
                            >
                              <td className="px-4 py-4 whitespace-nowrap">
                                <input
                                  type="checkbox"
                                  checked={selectedRows.has(index)}
                                  onChange={() => handleSelectRow(index)}
                                  className="rounded border-gray-300 text-gray-800 focus:ring-gray-800"
                                />
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm font-medium text-gray-900">
                                  {costCode}
                                </div>
                                {costCodeDesc && (
                                  <div className="text-sm text-gray-500">
                                    {costCodeDesc}
                                  </div>
                                )}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm font-medium text-gray-900">
                                  {costType}
                                </div>
                                {costTypeAbbr && (
                                  <div className="text-sm text-gray-500">
                                    {costTypeAbbr}
                                  </div>
                                )}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                                {defaultFee.toFixed(2)}%
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="flex items-center space-x-2">
                                  <input
                                    type="number"
                                    value={(function() {
                                      const settingKey = `${setting.cost_code_vuid}-${setting.cost_type_vuid}`;
                                      return unsavedFeeChanges[settingKey] !== undefined 
                                        ? unsavedFeeChanges[settingKey] 
                                        : feePercentage;
                                    })()}
                                    onChange={(e) => handleFeePercentageInputChange(setting, e.target.value)}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter') {
                                        e.preventDefault();
                                        handleFeePercentageSave(setting);
                                      }
                                    }}
                                    step="0.01"
                                    min="0"
                                    className="w-24 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-800 focus:border-transparent"
                                  />
                                  {(function() {
                                    const settingKey = `${setting.cost_code_vuid}-${setting.cost_type_vuid}`;
                                    const hasUnsavedChanges = unsavedFeeChanges[settingKey] !== undefined && 
                                                              unsavedFeeChanges[settingKey] !== feePercentage;
                                    return hasUnsavedChanges ? (
                                      <button
                                        onClick={() => handleFeePercentageSave(setting)}
                                        className="px-2 py-1 bg-green-600 text-white text-xs font-semibold rounded hover:bg-green-700 transition-colors"
                                        data-tooltip="Save changes"
                                      >
                                        Save
                                      </button>
                                    ) : null;
                                  })()}
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                {isOverride ? (
                                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                                    Override
                                  </span>
                                ) : (
                                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                    Default
                                  </span>
                                )}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                                {setting.updated_at ? (
                                  new Date(setting.updated_at).toLocaleString('en-US', {
                                    month: '2-digit',
                                    day: '2-digit',
                                    year: 'numeric',
                                    hour: '2-digit',
                                    minute: '2-digit',
                                    hour12: true
                                  })
                                ) : (
                                  <span className="text-gray-400">Never</span>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  {/* Summary */}
                  {filteredSettings.length > 0 && (
                    <div className="mt-6 pt-6 border-t border-gray-200">
                      <p className="text-sm text-gray-600">
                        Showing {filteredSettings.length} of {feeSettings.length} fee setting(s)
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ContractFeeSettings;




